import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/atoms/Button';
import { Input, Select, DatePicker, Upload, Radio } from 'antd';
import type { UploadFile } from 'antd';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { QRScanner } from '../../components/molecules/QRScanner';
import { ScanLine } from 'lucide-react';

const { TextArea } = Input;
const { Option } = Select;

interface PetFormData {
  name: string;
  species: 'dog' | 'cat' | 'bird' | 'other' | '';
  breed: string;
  gender: 'male' | 'female' | '';
  dateOfBirth: string;
  weight: string;
  color: string;
  microchipId: string;
  photo: UploadFile[];
  medicalHistory: string;
  allergies: string;
}

const PetOwnerSetup: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [hasExistingPet, setHasExistingPet] = useState<boolean | null>(null);
  const [existingPetId, setExistingPetId] = useState('');
  const [isQRScannerOpen, setIsQRScannerOpen] = useState(false);

  const [formData, setFormData] = useState<PetFormData>({
    name: '',
    species: '',
    breed: '',
    gender: '',
    dateOfBirth: '',
    weight: '',
    color: '',
    microchipId: '',
    photo: [],
    medicalHistory: '',
    allergies: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const speciesOptions = [
    { value: 'dog', label: t('petOwnerSetup.species.dog') },
    { value: 'cat', label: t('petOwnerSetup.species.cat') },
    { value: 'bird', label: t('petOwnerSetup.species.bird') },
    { value: 'other', label: t('petOwnerSetup.species.other') },
  ];

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('petOwnerSetup.validation.nameRequired');
    }

    if (!formData.species) {
      newErrors.species = t('petOwnerSetup.validation.speciesRequired');
    }

    if (!formData.breed.trim()) {
      newErrors.breed = t('petOwnerSetup.validation.breedRequired');
    }

    if (!formData.gender) {
      newErrors.gender = t('petOwnerSetup.validation.genderRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmitNewPet = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      // TODO: Call API to create pet
      // const response = await petService.createPet(formData);

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Mark onboarding as completed
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        user.hasCompletedOnboarding = true;
        localStorage.setItem('user', JSON.stringify(user));
      }

      toast.success(t('petOwnerSetup.success'));
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Failed to create pet:', error);
      toast.error(t('petOwnerSetup.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLinkExistingPet = async () => {
    if (!existingPetId.trim()) {
      toast.error(t('petOwnerSetup.validation.petIdRequired'));
      return;
    }

    setIsLoading(true);
    try {
      // TODO: Call API to link existing pet
      // const response = await petService.linkPet(existingPetId);

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      toast.success(t('petOwnerSetup.linkSuccess'));
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Failed to link pet:', error);
      toast.error(t('petOwnerSetup.linkError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleQRScanSuccess = (decodedText: string) => {
    setExistingPetId(decodedText);
    toast.success(t('qrScanner.scanSuccess'));
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 px-4 py-12">
      <div className="mx-auto max-w-3xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="mb-4 flex justify-center">
            <div className="rounded-full bg-indigo-100 p-4">
              <svg className="h-12 w-12 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
              </svg>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 sm:text-4xl">
            {t('petOwnerSetup.title')}
          </h1>
          <p className="mt-3 text-lg text-gray-600">
            {t('petOwnerSetup.subtitle')}
          </p>
        </div>

        {/* Question: Has Existing Pet? */}
        {hasExistingPet === null && (
          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-lg">
            <h2 className="mb-6 text-center text-xl font-bold text-gray-900">
              {t('petOwnerSetup.hasExistingPetQuestion')}
            </h2>

            <div className="grid gap-4 md:grid-cols-2">
              <button
                onClick={() => setHasExistingPet(false)}
                className="group rounded-xl border-2 border-gray-200 p-6 text-center transition-all hover:border-indigo-600 hover:shadow-lg"
              >
                <div className="mb-3 text-4xl">🐾</div>
                <h3 className="mb-2 text-lg font-bold text-gray-900">
                  {t('petOwnerSetup.newPetButton')}
                </h3>
                <p className="text-sm text-gray-600">
                  {t('petOwnerSetup.newPetDescription')}
                </p>
              </button>

              <button
                onClick={() => setHasExistingPet(true)}
                className="group rounded-xl border-2 border-gray-200 p-6 text-center transition-all hover:border-indigo-600 hover:shadow-lg"
              >
                <div className="mb-3 text-4xl">🔗</div>
                <h3 className="mb-2 text-lg font-bold text-gray-900">
                  {t('petOwnerSetup.existingPetButton')}
                </h3>
                <p className="text-sm text-gray-600">
                  {t('petOwnerSetup.existingPetDescription')}
                </p>
              </button>
            </div>
          </div>
        )}

        {/* Form: Link Existing Pet */}
        {hasExistingPet === true && (
          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-lg">
            <button
              onClick={() => setHasExistingPet(null)}
              className="mb-4 text-sm text-gray-600 hover:text-gray-900"
            >
              ← {t('petOwnerSetup.backButton')}
            </button>

            <h2 className="mb-6 text-xl font-bold text-gray-900">
              {t('petOwnerSetup.linkExistingPetTitle')}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  {t('petOwnerSetup.petIdLabel')} <span className="text-red-500">*</span>
                </label>
                <Input
                  size="large"
                  placeholder={t('petOwnerSetup.petIdPlaceholder')}
                  value={existingPetId}
                  onChange={(e) => setExistingPetId(e.target.value)}
                  suffix={
                    <button
                      type="button"
                      onClick={() => setIsQRScannerOpen(true)}
                      className="flex items-center justify-center rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-indigo-600"
                      title={t('qrScanner.scanButton')}
                    >
                      <ScanLine size={20} />
                    </button>
                  }
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t('petOwnerSetup.petIdHelper')}
                </p>
              </div>

              <Button
                variant="primary"
                size="large"
                onClick={handleLinkExistingPet}
                isLoading={isLoading}
                fullWidth
              >
                {t('petOwnerSetup.linkButton')}
              </Button>
            </div>
          </div>
        )}

        {/* Form: Register New Pet */}
        {hasExistingPet === false && (
          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-lg">
            <button
              onClick={() => setHasExistingPet(null)}
              className="mb-4 text-sm text-gray-600 hover:text-gray-900"
            >
              ← {t('petOwnerSetup.backButton')}
            </button>

            <h2 className="mb-6 text-xl font-bold text-gray-900">
              {t('petOwnerSetup.registerNewPetTitle')}
            </h2>

            <div className="space-y-6">
              {/* Pet Photo */}
              <div className="flex justify-center">
                <Upload
                  listType="picture-circle"
                  fileList={formData.photo}
                  onChange={({ fileList }) => handleInputChange('photo', fileList)}
                  beforeUpload={() => false}
                  maxCount={1}
                >
                  {formData.photo.length === 0 && (
                    <div>
                      <div className="text-3xl">📸</div>
                      <div className="mt-2 text-xs">{t('petOwnerSetup.uploadPhotoButton')}</div>
                    </div>
                  )}
                </Upload>
              </div>

              {/* Basic Information */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    {t('petOwnerSetup.nameLabel')} <span className="text-red-500">*</span>
                  </label>
                  <Input
                    size="large"
                    placeholder={t('petOwnerSetup.namePlaceholder')}
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    status={errors.name ? 'error' : ''}
                  />
                  {errors.name && (
                    <p className="mt-1 text-xs text-red-500">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    {t('petOwnerSetup.speciesLabel')} <span className="text-red-500">*</span>
                  </label>
                  <Select
                    size="large"
                    className="w-full"
                    placeholder={t('petOwnerSetup.speciesPlaceholder')}
                    value={formData.species || undefined}
                    onChange={(value) => handleInputChange('species', value)}
                    status={errors.species ? 'error' : ''}
                  >
                    {speciesOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                  {errors.species && (
                    <p className="mt-1 text-xs text-red-500">{errors.species}</p>
                  )}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    {t('petOwnerSetup.breedLabel')} <span className="text-red-500">*</span>
                  </label>
                  <Input
                    size="large"
                    placeholder={t('petOwnerSetup.breedPlaceholder')}
                    value={formData.breed}
                    onChange={(e) => handleInputChange('breed', e.target.value)}
                    status={errors.breed ? 'error' : ''}
                  />
                  {errors.breed && (
                    <p className="mt-1 text-xs text-red-500">{errors.breed}</p>
                  )}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    {t('petOwnerSetup.genderLabel')} <span className="text-red-500">*</span>
                  </label>
                  <Radio.Group
                    value={formData.gender}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                    className="w-full"
                  >
                    <Radio value="male">{t('petOwnerSetup.gender.male')}</Radio>
                    <Radio value="female">{t('petOwnerSetup.gender.female')}</Radio>
                  </Radio.Group>
                  {errors.gender && (
                    <p className="mt-1 text-xs text-red-500">{errors.gender}</p>
                  )}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    {t('petOwnerSetup.dateOfBirthLabel')}
                  </label>
                  <DatePicker
                    size="large"
                    className="w-full"
                    placeholder={t('petOwnerSetup.dateOfBirthPlaceholder')}
                    value={formData.dateOfBirth ? dayjs(formData.dateOfBirth) : null}
                    onChange={(date, dateString) => handleInputChange('dateOfBirth', dateString)}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    {t('petOwnerSetup.weightLabel')}
                  </label>
                  <Input
                    size="large"
                    placeholder={t('petOwnerSetup.weightPlaceholder')}
                    value={formData.weight}
                    onChange={(e) => handleInputChange('weight', e.target.value)}
                    suffix="kg"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    {t('petOwnerSetup.colorLabel')}
                  </label>
                  <Input
                    size="large"
                    placeholder={t('petOwnerSetup.colorPlaceholder')}
                    value={formData.color}
                    onChange={(e) => handleInputChange('color', e.target.value)}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    {t('petOwnerSetup.microchipIdLabel')}
                  </label>
                  <Input
                    size="large"
                    placeholder={t('petOwnerSetup.microchipIdPlaceholder')}
                    value={formData.microchipId}
                    onChange={(e) => handleInputChange('microchipId', e.target.value)}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    {t('petOwnerSetup.medicalHistoryLabel')}
                  </label>
                  <TextArea
                    rows={3}
                    placeholder={t('petOwnerSetup.medicalHistoryPlaceholder')}
                    value={formData.medicalHistory}
                    onChange={(e) => handleInputChange('medicalHistory', e.target.value)}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    {t('petOwnerSetup.allergiesLabel')}
                  </label>
                  <TextArea
                    rows={2}
                    placeholder={t('petOwnerSetup.allergiesPlaceholder')}
                    value={formData.allergies}
                    onChange={(e) => handleInputChange('allergies', e.target.value)}
                  />
                </div>
              </div>

              <Button
                variant="primary"
                size="large"
                onClick={handleSubmitNewPet}
                isLoading={isLoading}
                fullWidth
              >
                {t('petOwnerSetup.submitButton')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PetOwnerSetup;
